/**
 * Utility functions for formatting bookmark metadata
 */

/**
 * Format view count with smart rounding
 * @param {number} views - The number of views
 * @returns {string} Formatted view count (e.g., "1k views", "3.5M views")
 */
export function formatViews(views) {
  if (!views || views === 0) return '0 views'
  
  if (views < 1000) {
    return `${views} views`
  } else if (views < 1000000) {
    const k = views / 1000
    // Round to 1 decimal place if needed
    const formatted = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)
    return `${formatted}k views`
  } else if (views < 1000000000) {
    const m = views / 1000000
    const formatted = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)
    return `${formatted}M views`
  } else {
    const b = views / 1000000000
    const formatted = b % 1 === 0 ? b.toFixed(0) : b.toFixed(1)
    return `${formatted}B views`
  }
}

/**
 * Format duration in seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "45 seconds", "25 minutes", "2h 15m")
 */
export function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0 seconds'
  
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    return `${minutes}m ${remainingSeconds}s`
  } else {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (minutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }
    return `${hours}h ${minutes}m`
  }
}

/**
 * Format rating as percentage
 * @param {number} rating - Rating value (0-1)
 * @returns {string} Formatted rating percentage
 */
export function formatRating(rating) {
  if (rating === null || rating === undefined) return 'N/A'
  const percentage = Math.round(rating * 100)
  return `${percentage}%`
}

/**
 * Format date using browser locale
 * @param {string|Date} date - Date string or Date object
 * @param {object} options - Optional formatting options
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
  if (!date) return ''
  
  try {
    let dateObj;
    if (typeof date === 'number') {
       // Check if it's seconds (Unix timestamp) or milliseconds
       // 100000000000 is roughly year 1973 in milliseconds, 
       // but year 5138 in seconds. 
       // Current timestamp ~ 1.7e9 (seconds), ~ 1.7e12 (ms)
       if (date < 100000000000) {
         dateObj = new Date(date * 1000)
       } else {
         dateObj = new Date(date)
       }
    } else {
       dateObj = typeof date === 'string' ? new Date(date) : date
    }
    
    // Check if date is valid
    if (!dateObj || isNaN(dateObj.getTime())) return ''
    
    // Default options for a nice, localized format
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    }
    
    // Use the browser's locale automatically
    return dateObj.toLocaleDateString(undefined, defaultOptions)
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

/**
 * Format date and time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(date) {
  if (!date) return ''
  
  try {
    let dateObj;
    if (typeof date === 'number') {
       if (date < 100000000000) {
         dateObj = new Date(date * 1000)
       } else {
         dateObj = new Date(date)
       }
    } else {
       dateObj = typeof date === 'string' ? new Date(date) : date
    }
    
    // Check if date is valid
    if (!dateObj || isNaN(dateObj.getTime())) return ''
    
    // Default options for a nice, localized format
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    
    // Use the browser's locale automatically
    return dateObj.toLocaleString(undefined, defaultOptions)
  } catch (error) {
    console.error('Error formatting date time:', error)
    return ''
  }
}

/**
 * Format date in long format using browser locale
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date string in long format
 */
export function formatDateLong(date) {
  return formatDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

/**
 * Format date in short format using browser locale
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date string in short format
 */
export function formatDateShort(date) {
  return formatDate(date, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
}

/**
 * Format recipe time (prep, cook, total) in minutes
 * @param {number} minutes - Time in minutes
 * @returns {string} Formatted time (e.g., "45 min", "1h 30min")
 */
export function formatRecipeTime(minutes) {
  const mins = parseInt(minutes, 10);
  if (isNaN(mins) || mins <= 0) return ''
  
  if (mins < 60) {
    return `${mins} min`
  }
  
  const h = Math.floor(mins / 60)
  const m = mins % 60

  if (m === 0) {
    return `${h}h`
  }

  return `${h}h ${m}min`
}

/**
 * Convert a metadata attribute key (snake_case or camelCase) to a Title Case display label,
 * e.g. "recommendation_count" → "Recommendation Count".
 * @param {string} key - The attribute key
 * @returns {string} Title Case label
 */
export function formatAttributeName(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

/**
 * True if the value is a single http(s) URL string (after trimming). Used to render TEXTUAL
 * metadata values as clickable links. Requires no internal whitespace so we never linkify a
 * sentence that merely contains a URL, and only http/https (no javascript:/data: schemes).
 */
export function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\/\S+$/i.test(value.trim())
}

/**
 * Render JSON-typed metadata (an object, or an array of objects) as a readable string instead of
 * the useless "[object Object]". Arrays become a comma-separated list of best-effort labels.
 */
export function formatJsonMetadata(content) {
  if (content === null || content === undefined) return ''
  if (Array.isArray(content)) {
    return content.map(formatJsonEntry).filter(Boolean).join(', ')
  }
  if (typeof content === 'object') {
    return formatJsonEntry(content)
  }
  return String(content)
}

function formatJsonEntry(entry) {
  if (entry === null || entry === undefined) return ''
  if (typeof entry !== 'object') return String(entry)
  // recommendation provenance shape: { number, byline, title, url, date, context }
  if (entry.number !== null && entry.number !== undefined) {
    return `#${entry.number}${entry.byline ? ' ' + entry.byline : ''}`
  }
  return entry.title || entry.name || entry.label || entry.url || ''
}

// ─── Streaming availability (TMDB/JustWatch) ────────────────────────────────

/**
 * The region subtag of the browser locale (e.g. "de-AT" → "AT"), or null. The app's
 * i18n locale is deliberately language-only, so this is the only place the browser's
 * country hint is used — as a display-time default until the user picks a country.
 */
export function getBrowserRegion(language = (typeof navigator !== 'undefined' ? navigator.language : null)) {
  if (typeof language !== 'string') return null
  const match = language.match(/^[A-Za-z]{2,3}(?:-[A-Za-z]{4})?-([A-Za-z]{2})(?:-|$)/)
  return match ? match[1].toUpperCase() : null
}

/**
 * Decide what the card's single streaming chip shows for a viewer.
 *
 * @param {object|null} regions - the stored `streaming_providers.content.regions` map
 *   ({ US: { flatrate: [8, 337], rent: [2], buy: [3] }, ... }, provider IDs only)
 * @param {string} country - the viewer's effective two-letter country
 * @param {Set<number>} subscribedIds - provider ids the viewer subscribes to
 * @returns {{status: 'subscribed'|'stream'|'rentbuy'|null, providerId: number|null, extraCount: number}}
 *   - subscribed: on ≥1 of the viewer's services (providerId = first subscribed match, extraCount = further subscribed matches)
 *   - stream: streamable (flatrate/free/ads) but not on their services (providerId = first provider, extraCount = further providers)
 *   - rentbuy: only rent/buy offers exist in their country
 *   - null: nothing to show
 */
export function pickStreamingOffer(regions, country, subscribedIds = new Set()) {
  const offers = regions && country ? regions[country] : null
  if (!offers) return { status: null, providerId: null, extraCount: 0 }

  // "Streamable" = included with a service: subscription, free, or ad-supported.
  const streamable = []
  for (const monetization of ['flatrate', 'free', 'ads']) {
    for (const id of offers[monetization] || []) {
      if (!streamable.includes(id)) streamable.push(id)
    }
  }

  const subscribedMatches = streamable.filter(id => subscribedIds.has(id))
  if (subscribedMatches.length > 0) {
    return { status: 'subscribed', providerId: subscribedMatches[0], extraCount: subscribedMatches.length - 1 }
  }
  if (streamable.length > 0) {
    return { status: 'stream', providerId: streamable[0], extraCount: streamable.length - 1 }
  }
  if ((offers.rent || []).length > 0 || (offers.buy || []).length > 0) {
    return { status: 'rentbuy', providerId: null, extraCount: 0 }
  }
  return { status: null, providerId: null, extraCount: 0 }
}

/** Provider logo URL on TMDB's image CDN (logos are not bundled). */
export function tmdbLogoUrl(logoPath, size = 'w45') {
  if (!logoPath || typeof logoPath !== 'string') return null
  return `https://image.tmdb.org/t/p/${size}${logoPath.startsWith('/') ? '' : '/'}${logoPath}`
}

/** The TMDB "where to watch" page for a title (the full offer list lives there). */
export function tmdbWatchUrl(tmdbType, tmdbId, country) {
  if (!tmdbId || (tmdbType !== 'movie' && tmdbType !== 'tv')) return null
  const locale = country && /^[A-Za-z]{2}$/.test(country) ? `?locale=${country.toUpperCase()}` : ''
  return `https://www.themoviedb.org/${tmdbType}/${tmdbId}/watch${locale}`
}
